/**
 * AttentionDoneDialog (KAN-750, KAN-852)
 *
 * "Назначить в работу" in the attention block asks which sales manager takes
 * the client to work: a manager picker (search, active managers, "Без менеджера").
 * Default: the client's current manager, for a client without one
 * `defaultManager` (the cabinet session manager), else none. "Назначить в работу"
 * confirms with the chosen manager id (null: no manager), "Отмена" closes
 * without a request.
 * KAN-869: mode "handoff" (a regular manager hands an own client to another
 * manager): title and confirm say "Передать", `excludeIds` hides the manager
 * themselves, nothing is preselected and confirm needs a chosen manager.
 */
import { useEffect, useState } from 'react'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ManagerSelect } from '@/components/managers/ManagerSelect'
import { useManagers } from '@/hooks/useManagers'
import type { AttentionItem } from '@/services/api/dashboard'
import type { ManagerBrief } from '@/types/manager'
import { ASSIGN_TO_WORK_LABEL } from '@/lib/workActions'
import { HAND_OFF_LABEL } from '@/lib/clientManagerRights'
import { attentionItemTitle } from './attentionFormat'

export const NO_MANAGER_OPTION = 'Без менеджера'

export type AttentionDoneDialogMode = 'assign' | 'handoff'

interface AttentionDoneDialogProps {
  /** Row being closed; null: the dialog is closed. */
  item: AttentionItem | null
  /** The chosen manager comes second (KAN-869: name for the toast). */
  onConfirm: (managerId: string | null, manager: ManagerBrief | null) => void
  onCancel: () => void
  /** KAN-852: default manager for a client without one; null: none. */
  defaultManager?: ManagerBrief | null
  /** KAN-869: "handoff" for a regular manager handing an own client over. */
  mode?: AttentionDoneDialogMode
  /** KAN-869: managers not offered in the picker. */
  excludeIds?: readonly string[]
}

export function AttentionDoneDialog({
  item,
  onConfirm,
  onCancel,
  defaultManager = null,
  mode = 'assign',
  excludeIds,
}: AttentionDoneDialogProps) {
  const handoff = mode === 'handoff'
  const [manager, setManager] = useState<ManagerBrief | null>(null)
  const { data: managers = [] } = useManagers('true')

  useEffect(() => {
    if (item) setManager(handoff ? null : (item.manager ?? defaultManager))
    // Only a newly opened row resets the choice, not a session manager change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item])

  const select = (managerId: string | null) => {
    if (managerId === null) {
      setManager(null)
      return
    }
    const found = managers.find((m) => m.id === managerId)
    if (found) setManager({ id: found.id, full_name: found.full_name })
  }

  return (
    <Dialog
      open={item !== null}
      onOpenChange={(open) => {
        if (!open) onCancel()
      }}
    >
      <DialogContent className="max-w-md" data-testid="attention-done-dialog">
        <DialogHeader>
          <DialogTitle>
            {handoff ? 'Передать клиента' : ASSIGN_TO_WORK_LABEL}: {item ? attentionItemTitle(item) : ''}
          </DialogTitle>
          <DialogDescription>
            {handoff
              ? 'Выберите менеджера, которому передать клиента.'
              : 'Выберите менеджера, который возьмёт клиента в работу. Бот больше не пишет клиенту первым.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <span className="text-sm text-gray-500">Менеджер</span>
          <ManagerSelect
            value={manager}
            onSelect={select}
            noneLabel={NO_MANAGER_OPTION}
            testId="attention-done-manager"
            excludeIds={excludeIds}
            showNone={!handoff}
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onCancel}>
            Отмена
          </Button>
          <Button
            type="button"
            data-testid="attention-done-confirm"
            disabled={handoff && manager === null}
            onClick={() => onConfirm(manager?.id ?? null, manager)}
          >
            <Check className="mr-1 h-3.5 w-3.5" />
            {handoff ? HAND_OFF_LABEL : ASSIGN_TO_WORK_LABEL}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
